import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { collection } from "firebase/firestore";
import { db } from "../firebase";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import type { TypeSettings } from "../types";

interface TypeSettingsValue {
  byType: Record<string, TypeSettings>;
  /** False until the first snapshot (or a failure) — "nothing saved" vs "not read yet". */
  loaded: boolean;
}

const TypeSettingsContext = createContext<TypeSettingsValue>({ byType: {}, loaded: false });

/**
 * Every type's saved settings, kept live. It's one small doc per type that
 * anyone has customised, so the whole collection is cheap to hold — and with
 * it held here, any screen can resolve a type's defaults without a read.
 */
export function TypeSettingsProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<TypeSettingsValue>({ byType: {}, loaded: false });

  useEffect(
    () =>
      onSnapshotResilient(
        collection(db, "typeSettings"),
        (snap) => {
          const next: Record<string, TypeSettings> = {};
          snap.forEach((d) => {
            next[d.id] = d.data() as TypeSettings;
          });
          setValue({ byType: next, loaded: true });
        },
        {
          label: "type settings",
          // Unreadable (rules not deployed yet, say): carry on with the built-in defaults.
          onError: () => setValue((prev) => ({ ...prev, loaded: true })),
        },
      ),
    [],
  );

  return <TypeSettingsContext.Provider value={value}>{children}</TypeSettingsContext.Provider>;
}

/** Every type's saved settings, by type. */
export function useAllTypeSettings(): Record<string, TypeSettings> {
  return useContext(TypeSettingsContext).byType;
}

/** Whether the saved settings have been read yet. */
export function useTypeSettingsLoaded(): boolean {
  return useContext(TypeSettingsContext).loaded;
}

/** One type's saved settings; null for a type nobody has customised, or none. */
export function useTypeSettings(type: string | null | undefined): TypeSettings | null {
  const { byType } = useContext(TypeSettingsContext);
  return (type && byType[type]) || null;
}
